import { useState, useEffect } from 'react';
import type { UseQueryResult } from 'react-query';
import { useQuery } from 'react-query';
import { getAnonymousUserID as getAnonIdFromSegment } from '@hotjar/webapp/domains/common/tracking/segment';
import { get as getAppState, set as setAppState } from '@hotjar/webapp/domains/common/app-state';
import type { ApiBaseResponse } from '@hotjar/webapp/domains/common/api';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';

interface AnonymousIdProps {
  anonymousId?: string;
  isLoading: boolean;
}

const ANONYMOUS_ID_QUERY_KEY = 'anonymous-id-query';

const CACHE_TIME = 86400000; // 24h

export const APP_STATE_NAMESPACE = 'EXPERIMENTATION';

export const APP_STATE_KEY = 'ANON_ID';

export const preserveAnonymousId = (
  userId: number,
  anonymousId: string,
): Promise<ApiBaseResponse> =>
  setAppState(userId, {
    namespace: APP_STATE_NAMESPACE,
    key: APP_STATE_KEY,
    value: anonymousId,
  });

export const useAnonymousId = (anonId?: string): AnonymousIdProps => {
  const { getAnonymousUserID } = useTrack();
  const [isLoading, setIsLoading] = useState(!anonId);
  const [anonymousId, setAnonymousId] = useState(anonId);

  useEffect(() => {
    if (!anonymousId) {
      getAnonymousUserID()
        .then((id) => {
          setAnonymousId(id as string);
          setIsLoading(false);
        })
        .catch(() => setIsLoading(false));
    }
  }, [anonymousId, setAnonymousId, setIsLoading, getAnonymousUserID]);

  return { anonymousId, isLoading };
};

const getPreservedAnonymousId = async (userId: number): Promise<string | undefined> => {
  const state = await getAppState(userId);
  const fallbackAnonymousId = (await getAnonIdFromSegment()) as string;
  const preservedAnonymousId =
    state && state[APP_STATE_NAMESPACE] && state[APP_STATE_NAMESPACE][APP_STATE_KEY];
  return preservedAnonymousId || fallbackAnonymousId;
};

export const usePreservedAnonymousId = (
  userId?: number,
): UseQueryResult<string | undefined, Error> => {
  return useQuery(
    [ANONYMOUS_ID_QUERY_KEY, { userId }],
    () => getPreservedAnonymousId(userId as number),
    {
      retry: false,
      enabled: !!userId,
      cacheTime: CACHE_TIME,
      refetchOnWindowFocus: false,
    },
  );
};
